import { IApiError } from './Api';
import {
  CalculatorParticipantType,
  CalculatorSplitMethod,
  IBeneficiary,
  ICalculatorParticipant,
  ICalculatorRule,
  ICalculatorRuleRemainder,
  ICalculatorRules,
  ICalculatorRuleSharePolicy,
  ICalculatorRuleSharePolicyShare,
  ICategoryGroup,
  ICreateUpdateCalculatorRules,
  IRemainderBeneficiary,
  IRemainderCondition,
  RuleFundSource,
  ShareEligibilityMethod,
  ShareRuleType,
  TipCalculatorMethod,
} from './CalculatorRule';
import { Days, IDaySegment } from './DaySegment';
import { IPayee } from './Employee';
import { IntegrationType, IPOSIntegration, IPOSProvider, IPOSResults, IPOSSegment, IPOSTicket, IPOSTicketCategory, IPOSTicketIgnoreOverride, IPOSTicketSplitOverride, ISplitParticipant, TicketOverrideType } from './Integration';
import { IJob } from './Job';
import { IPayPeriod } from './Payout';
import { IDaySegmentGracePeriodPolicy, ISiteSettings } from './Site';

export const dayOptions = [
  { label: 'Sun', value: Days.SUNDAY },
  { label: 'Mon', value: Days.MONDAY },
  { label: 'Tue', value: Days.TUESDAY },
  { label: 'Wed', value: Days.WEDNESDAY },
  { label: 'Thu', value: Days.THURSDAY },
  { label: 'Fri', value: Days.FRIDAY },
  { label: 'Sat', value: Days.SATURDAY },
];

export enum TipCalculationsTab {
  TIP_SHARES = 'tip-shares',
  DEFAULT_POOL = 'default-pool',
  POINT_VALUES = 'point-values',
}

export enum SourceType {
  AUTO_GRATUITIES = 'autoGratuity',
  TIP = 'tip',
}

export enum SourcePaymentType {
  CASH = 'cash',
  CREDIT = 'creditCard',
  CREDIT_FEE = 'creditCardFee',
}

export enum TipCalculatorReceivedDistributionType {
  REMAINDER = 'remainder',
  SHARE = 'share',
}

export enum TipCalculatorWarningType {
  CAPPED_SHARE = 'capped_share',
  CAPPED_REMAINDER = 'capped_remainder',
  UNALLOCATED_REMAINDER = 'unallocated_remainder',
}

export interface ITipCalculatorBaseTotals {
  gross: number;
  net: number;
  tip: { gross: number; net: number; cash: number; creditCard: number; creditCardFee: number };
  autoGratuity: { gross: number; net: number; cash: number; creditCard: number; creditCardFee: number };
}

interface ITipCalculatorTicketTotals extends ITipCalculatorBaseTotals {}

interface ITipCalculatorEmployeeTotals extends ITipCalculatorBaseTotals {
  totalMinWorked: number;
}

interface ITipCalculatorTicketCategory {
  name: string;
  price: number;
  quantity: number;
  discounts: number;
  tax: number;
  total: number;
}

interface ITipCalculatorTicketSplitOverrideShare {
  participant: ISplitParticipant;
  percent: number;
  gross: number;
  tip: number;
  autoGratuity: number;
  sources: RuleFundSource[];
}

interface ITipCalculatorTicketSplitOverride {
  type: TicketOverrideType.SPLIT;
  shares: ITipCalculatorTicketSplitOverrideShare[];
}

interface ITipCalculatorTicketIgnoreOverride {
  type: TicketOverrideType.IGNORE;
}

/* BEGIN -- Tip Calculator Request -- BEGIN */
export interface ITipCalculatorRequest {
  segmentId: string;
  segment: string;
  start: string;
  end: string;
  employees: ITipCalculatorRequestEmployee[];
  participants?: ICalculatorParticipant[];
  rules: ICalculatorRule[];
  categoryGroups: ICategoryGroup[];
}

export interface ITipCalculatorRequestEmployee {
  id: string;
  name: string;
  job: IJob;
  provider: ITipCalculatorResultEmployeeProvider;
  schedules: ITipCalculatorRequestEmployeeSchedule[];
  tickets: ITipCalculatorRequestTicket[];
  totals: ITipCalculatorEmployeeTotals;
}

export interface ITipCalculatorRequestEmployeeSchedule {
  start: string;
  end: string;
}

export interface ITipCalculatorRequestTicket {
  ticketId: string;
  displayId: string;
  forcedMatch: boolean;
  openedAt: string;
  closedAt: string;
  paidAt: string;
  processingTime: string;
  net: number;
  gross: number;
  categories?: ITipCalculatorTicketCategory[];
  override?: ITipCalculatorTicketIgnoreOverride | ITipCalculatorTicketSplitOverride;
  totals: ITipCalculatorTicketTotals;
}
/* END -- Tip Calculator Request -- END */

/* BEGIN Tip Calculator Results -- BEGIN */
export interface ITipCalculatorResult {
  segmentId: string;
  segment: string;
  start: string;
  end: string;
  creditCardPercentFee?: number;
  employees: ITipCalculatorResultEmployee[];
  participants: ITipCalculatorResultParticipant[];
  rules: ICalculatorRule[];
  pools: ITipCalculatorResultPool[];
  totals: ITipCalculatorResultTotals;
  meta: ITipCalculatorResultMeta;
  warnings: ITipCalculatorResultWarning[];
}

export interface ITipCalculatorFriendlyErrorItem {
  ticketDisplayId?: string;
  employeeName?: string;
  description: string;
}

export interface ITipCalculatorErrorGroup {
  errors: IApiError[];
  tipCalcRequests: ITipCalculatorRequest[];
}

export interface ITipCalculatorErrorsByRequestPayload {
  errorsByRequest: ITipCalculatorErrorGroup[];
}

export type ITipCalculatorErrorPayload = IApiError[] | { errors: IApiError[]; tipCalcRequests?: ITipCalculatorRequest[] } | ITipCalculatorErrorsByRequestPayload;

export interface ITipCalculatorResultMeta {
  categoryGroups: ICategoryGroup[];
  participants: ICalculatorParticipant[];
}

export interface ITipCalculatorResultCalculatedTotals {
  total: number;
  autoGratuities: number;
  tips: number;
}

export interface ITipCalculatorResultCategory {
  name: string;
  price: number;
  quantity: number;
  discounts: number;
  tax: number;
  total: number;
  type: ShareRuleType;
}

export interface ITipCalculatorResultContribution {
  category?: string;
  eligibleAmount: number;
  eligibilityMethod: ShareEligibilityMethod;
  beneficiaries: { name: string; type: CalculatorParticipantType; split: CalculatorSplitMethod }[];
  share: number;
  sources: { source: RuleFundSource; total: number }[];
  total: number;
  type: ShareRuleType;
}

export interface ITipCalculatorResultReceived {
  category?: string;
  conditions?: IRemainderCondition[];
  distribution: TipCalculatorReceivedDistributionType;
  eligibilityMethod: ShareEligibilityMethod;
  from: ICalculatorParticipant;
  share: number;
  sources: { source: RuleFundSource; total: number }[];
  split: CalculatorSplitMethod;
  total: number;
  type: ShareRuleType;
}

export interface ITipCalculatorResultIgnoredCheck {
  displayId: string;
  ticketId: string;
  totals: ITipCalculatorBaseTotals;
}

export interface ITipCalculatorResultSplitCheckContribution {
  displayId: string;
  ticketId: string;
  totals: ITipCalculatorBaseTotals;
}

export interface ITipCalculatorResultSplitCheckReceived {
  displayId: string;
  ticketId: string;
  totals: ITipCalculatorResultCalculatedTotals;
  percent: number;
  sources: RuleFundSource[];
}

export interface ITipCalculatorResultSplitChecks {
  contributions: ITipCalculatorResultSplitCheckContribution[];
  received: ITipCalculatorResultSplitCheckReceived[];
}

export interface ITipCalculatorResultEmployeeProvider {
  id: string;
  type: IntegrationType;
}

export interface ITipCalculatorResultEmployee {
  id: string;
  name: string;
  provider?: ITipCalculatorResultEmployeeProvider;
  calculated: ITipCalculatorResultCalculatedTotals;
  categories: ITipCalculatorResultCategory[];
  contributions?: ITipCalculatorResultContribution[];
  ignoredChecks?: ITipCalculatorResultIgnoredCheck[];
  job: IJob;
  received: ITipCalculatorResultReceived[];
  retained: ITipCalculatorResultCalculatedTotals;
  segmentId: string;
  splitChecks?: ITipCalculatorResultSplitChecks;
  totals: ITipCalculatorResultEmployeeTotals;
}

export interface ITipCalculatorResultParticipant {
  id: string;
  name: string;
  calculated: ITipCalculatorResultCalculatedTotals;
  categories: ITipCalculatorResultCategory[];
  contributions?: ITipCalculatorResultContribution[];
  received: ITipCalculatorResultReceived[];
  retained: ITipCalculatorResultCalculatedTotals;
  segmentId: string;
  splitChecks?: ITipCalculatorResultSplitChecks;
  totals: ITipCalculatorBaseTotals;
  type: CalculatorParticipantType;
}

export interface ITipCalculatorResultEmployeeTotals extends ITipCalculatorBaseTotals {
  totalMinWorked: number;
}

export interface ITipCalculatorResultPool {
  calculated: ITipCalculatorResultCalculatedTotals;
  categories: ITipCalculatorResultCategory[];
  contributions: ITipCalculatorResultContribution[];
  job: IJob;
  received: ITipCalculatorResultReceived[];
  retained: ITipCalculatorResultCalculatedTotals;
  totals: ITipCalculatorResultEmployeeTotals;
}

export interface ITipCalculatorResultTotals {
  allocated: ITipCalculatorResultCalculatedTotals;
  collected: ITipCalculatorResultCalculatedTotals;
}

export interface ITipCalculatorResultFunds {
  allocated: number;
  disbursed: number;
  diff: number;
}

interface ITipCalculatorResultWarningBase {
  type: TipCalculatorWarningType;
  segment?: string;
  contributor: ICalculatorParticipant;
  funds?: ITipCalculatorResultFunds;
}

export interface ITipCalculatorResultWarningCappedShare extends ITipCalculatorResultWarningBase {
  type: TipCalculatorWarningType.CAPPED_SHARE;
  employee: { id: string; name: string };
  details: ITipCalculatorResultWarningCappedShareDetail[];
}

export interface ITipCalculatorResultWarningCappedShareDetailRule {
  category?: string;
  eligibilityMethod: ShareEligibilityMethod;
  share: number;
  sources: RuleFundSource[];
  type: ShareRuleType;
  beneficiaries: IBeneficiary[];
}

export interface ITipCalculatorResultWarningCappedShareDetail {
  funds: ITipCalculatorResultFunds;
  rule: ITipCalculatorResultWarningCappedShareDetailRule;
}

export interface ITipCalculatorResultWarningCappedRemainder extends ITipCalculatorResultWarningBase {
  type: TipCalculatorWarningType.CAPPED_REMAINDER;
  details: ITipCalculatorResultWarningCappedRemainderDetail[];
}

export interface ITipCalculatorResultWarningCappedRemainderDetail {
  funds: ITipCalculatorResultFunds;
  rule: { beneficiaries: IRemainderBeneficiary[] };
}

export interface ITipCalculatorResultWarningUnallocatedRemainder extends ITipCalculatorResultWarningBase {
  type: TipCalculatorWarningType.UNALLOCATED_REMAINDER;
  contributor: ICalculatorParticipant;
  beneficiaries: {
    participant: { name: string; type: CalculatorParticipantType };
    received: ITipCalculatorResultReceived[];
  }[];
  remainder: {
    total: ITipCalculatorResultCalculatedTotals;
    unallocated: ITipCalculatorResultCalculatedTotals;
  };
  isVirtual: boolean;
}

export type ITipCalculatorResultWarning = ITipCalculatorResultWarningCappedShare | ITipCalculatorResultWarningCappedRemainder | ITipCalculatorResultWarningUnallocatedRemainder;
/*  END -- Tip Calculator Results -- END */

/* BEGIN -- Tip Calculator Change Log --BEGIN */
export enum ChangeLogEntity {
  LABOR_DATA = 'labor_data',
  TIP_DATA = 'tip_data',
  CHECK = 'check',
  PAYMENT = 'payment',
}

export enum ChangeLogParticipantType {
  JOB = 'job',
  HOUSE_ACCOUNT = 'house_account',
  EMPLOYEE = 'employee',
}

interface ChangeLogParticipant {
  name: string;
  type: ChangeLogParticipantType;
  job?: string;
}

export enum ChangeLogAction {
  SEGMENT_DURATION_EDITED = 'segment_duration_edited',
  SEGMENT_REMOVED = 'segment_removed',
  REMOVED = 'removed',
  CASH_TIPS_EDITED = 'cash_tips_edited',
  CREDIT_CARD_TIPS_EDITED = 'credit_card_tips_edited',
  CASH_AUTOGRATS_EDITED = 'cash_autograts_edited',
  CREDIT_AUTOGRATS_EDITED = 'credit_autograts_edited',
  IGNORE = 'ignore',
  SPLIT = 'split',
  UNDO_OVERRIDE = 'undo_override',
  CASH_EDITED = 'cash_edited',
  HOLDBACK_EDITED = 'holdback_edited',
  CASH_PAYOUT_ENTERED = 'cash_payout_entered',
}

export interface LaborDataDetails {
  id: string;
  from?: number;
  to?: number;
}

export interface TipEditedDetails {
  id: string;
  from: number;
  to: number;
}

export interface HoldbackEditedDetails {
  id: string;
  from: number;
  to: number;
}

export interface CashPayoutDetails {
  id: string;
  from: number;
  to: number;
}

export interface CheckModifiedDetails {
  id: string;
  displayId: string;
  from?: number;
  to?: number;
}

type ChangeLogDetails = LaborDataDetails | TipEditedDetails | CheckModifiedDetails;

export interface ITipCalculatorChangeLogEntry {
  entity: ChangeLogEntity;
  action: ChangeLogAction;
  participant: ChangeLogParticipant;
  segment?: string;
  details?: ChangeLogDetails;
  timestamp: string;
}
/* END -- Tip Calculator Change Log --END */

export interface IUserPayoutCalculations {
  method: TipCalculatorMethod;
  payoutId: string;
  results: ITipCalculatorResult[];
}

export interface ICheck extends IPOSTicket {
  /** The check's position in the POS results it was built from, e.g. `'0.1.2'`. */
  key: string;
  segmentName: string;
  employeeName: string;
  selected?: boolean;
}

export interface ITipCalculator {
  posResults?: IPOSResults;
  paymentSet?: ICalculatorPaymentSet;
  payees?: ITipCalculatorPayee[];
  results?: ITipCalculatorResult[];
  changeLog?: ITipCalculatorChangeLogEntry[];
}

export interface ITipCalculatorEmployeeTicket {
  ticketId: string;
  displayId?: string;
  forcedMatch: boolean;
  processingTime: string;
  creditCardFee: number;
  tip: number;
  netTip: number;
  totals: {
    gross: number;
    net: number;
    tip: { gross: number; net: number; cash: number; creditCard: number; creditCardFee: number };
    autoGratuity: { gross: number; net: number; cash: number; creditCard: number; creditCardFee: number };
  };
  categories: IPOSTicketCategory[];
  override?: IPOSTicketIgnoreOverride | IPOSTicketSplitOverride;
  employee?: {
    employeeId: string;
    firstName: string;
    lastName: string;
    email: string;
    jobTitle: string;
    customerEmployeeId: string;
  };
}

export interface ICalculatorPaymentSet {
  date: string;
  siteId: string;
  segments: IDaySegment[];
  description?: string;
  payPeriod?: IPayPeriod;
}

export interface ICollectedTipsData {
  id: number;
  payee?: string;
  daySegment?: string;
  jobTitle?: string;
  cashTips?: number;
  cashAutograts?: number;
  ccTips?: number;
  ccAutograts?: number;
  ccFee?: number;
  net?: number;
}

export interface IReceivedTipsData {
  payee?: string;
  daySegment?: string;
  jobTitle?: string;
  duration?: number;
  receivedFrom?: string;
  receivedTips?: number;
  receivedAutograts?: number;
}

interface IFormCalculatorParticipant extends ICalculatorParticipant {
  id: string;
}

export interface IFormBeneficiary extends IBeneficiary {
  id: string;
}

export interface IFormRemainderBeneficiary extends IRemainderBeneficiary {
  id: string;
  source?: string;
  condition?: IRemainderCondition;
  participants: IFormCalculatorParticipant[];
}

export interface IFormCalculatorRule extends ICalculatorRule {
  id: string;
  value: any;
  isBenefitingFromShare?: boolean;
  isBenefitingFromDefault?: boolean;
}

export interface IFormValues extends Omit<ICreateUpdateCalculatorRules, 'rules'> {
  rules: IFormCalculatorRule[];
}

export interface ITipShareFormValues extends Omit<ICalculatorRule, 'sharePolicy'> {
  id: string;
  sharePolicy: IFormCalculatorRuleSharePolicy;
}

interface IFormCalculatorRuleSharePolicy extends Omit<ICalculatorRuleSharePolicy, 'shares'> {
  shares: IFormCalculatorRuleShare[];
  remainder: IFormCalculatorRuleRemainder;
}

export interface IFormCalculatorRuleShare extends Omit<ICalculatorRuleSharePolicyShare, 'beneficiaries'> {
  id: string;
  beneficiaries: IFormBeneficiary[];
}

interface IFormCalculatorRuleRemainder extends ICalculatorRuleRemainder {
  beneficiaries: IFormRemainderBeneficiary[];
}

export interface IFormDefaultPool {
  includeNoLabor: boolean;
  contributingJobs: IFormCalculatorParticipant[];
  benefitingJobs: IFormCalculatorParticipant[];
}

export enum TipCalculatorPayeeErrorType {
  NEGATIVE_NET_PAYMENT = 'negative_net_payment',
  MAX_PAYMENT_EXCEEDED = 'max_payment_exceeded',
  USER_SETUP_NOT_COMPLETE = 'user_setup_not_complete',
  INELIGIBLE_SELF_PAY = 'ineligible_self_pay',
  INELIGIBLE_PAYABLE = 'ineligible_payable',
  AUTO_CLOCKOUT = 'auto_clockout',
}

export interface ITipCalculatorPayeeError {
  type: TipCalculatorPayeeErrorType;
  preventSubmit: boolean;
}

export interface ITipCalculatorPayee extends IPayee {
  calculations: ITipCalculatorResultEmployee[];
  provider?: IPOSProvider;
  segments?: IPOSSegment[];
  errors?: ITipCalculatorPayeeError[];
}

export interface ITipCalculatorParticipant {
  name: string;
  type: CalculatorParticipantType;
  calculations: ITipCalculatorResultParticipant[];
  segments?: IPOSSegment[];
  gross: number;
  net: number;
  autoGratuity: number;
}

export interface IPoolReceivedFrom {
  distribution: TipCalculatorReceivedDistributionType;
  name: string;
  total: number;
  sources: string;
  category: string;
  split: CalculatorSplitMethod;
  eligibilityMethod: ShareEligibilityMethod;
  conditions?: IRemainderCondition[];
  sharePercent?: string;
}

export interface IPoolEmployeeRow {
  employee: ITipCalculatorResultEmployee;
  tips: number;
  autograts: number;
  total: number;
  percentWorked?: string;
}

export interface IPoolReport {
  employeeRows: IPoolEmployeeRow[];
  receivedFromRows: IPoolReceivedFrom[];
  totalMinWorked: number;
  totalReceivedTips: number;
  totalReceivedAutograts: number;
  totalReceived: number;
}

export interface IPoolJob {
  jobTitle: string;
  totalMinutes: number;
  totalTips: number;
}

export interface IPoolData {
  daySegment: string;
  totalMinutes: number;
  totalTips: number;
  pools: IPoolJob[];
  result: ITipCalculatorResult;
}

export enum TipCalculatorEligibilityMethodLabel {
  by_segment = 'segment',
  by_ticket = 'check',
}

interface IExportRuleSettings extends ICalculatorRules {
  tipCalculatorPercentFee: number;
}

export interface IExportCalculatorSettings {
  exportedAt: string;
  ruleSettings: IExportRuleSettings;
  segmentSettings: {
    businessDayStart: string;
    gracePeriodPolicy: IDaySegmentGracePeriodPolicy;
    segments: IDaySegment[];
  };
  site: {
    id: string;
    name: string;
  };
}

/* BEGIN -- Calculator Settings Gate & Comparison -- BEGIN */
/** Which of the three settings-gate states the manager is shown before a recalculation. */
export enum SettingsGateState {
  /** The snapshot is complete and differs from the site's current settings. The manager must choose. */
  CHANGED = 'changed',
  /** The snapshot is complete and matches. Informational. */
  UNCHANGED = 'unchanged',
  /** The snapshot predates full settings capture, so no comparison is possible. Informational. */
  INCOMPLETE = 'incomplete',
}

/** Which settings a recalculation should run with, once the manager has chosen. */
export enum CalculatorSettingsChoice {
  ORIGINAL = 'original',
  CURRENT = 'current',
}

/**
 * The gate's outcome for the current calculator run, held in calculator state so the override story
 * can read the manager's choice without re-plumbing the decision.
 */
/**
 * The day segment shape the override sends: the captured shape with `endsNextDay` optional.
 *
 * Declared here rather than reusing `IPaymentSetDaySegment` because `types/PaymentSet.ts` imports from this
 * module, and because the wire contract is genuinely looser — `account_services` keeps a separate
 * `CalculatorDaySegmentOverrideRequest` for the same reason, so a bundle captured before `endsNextDay`
 * existed is still accepted.
 */
export interface ICalculatorOverrideDaySegment {
  name: string;
  start: string;
  end: string;
  endsNextDay?: boolean;
}

/**
 * Settings bundle posted to `POST /v1/sites/:site_id/pos/:date` to reassemble POS data under the settings a
 * past calculation ran with. Mirrors `CalculatorSettingsOverrideRequest` in `account_services`; `ApiClient`
 * snake_cases the keys on the way out.
 *
 * Every field is independently optional and falls back to live site config on the server, because snapshots
 * gained fields over time — a bundle missing `daySegments` must still replay everything else it carries.
 */
export interface ICalculatorSettingsOverride {
  gracePeriodPolicy?: IDaySegmentGracePeriodPolicy;
  businessDayStart?: string;
  timezone?: string;
  creditCardPercentFee?: number;
  rules?: ICalculatorRule[];
  daySegments?: ICalculatorOverrideDaySegment[];
  integrations?: IPOSIntegration[];
  categoryGroups?: ICategoryGroup[];
  participants?: ICalculatorParticipant[];
}

/** The rules, sales groups and participants a calculation runs with — the site's live rules, or a snapshot's. */
export type ICalculatorRulesSource = Pick<ICalculatorRules, 'rules' | 'categoryGroups' | 'participants'>;

/** The calculator settings a stored payment snapshot can differ from the site's current configuration on. */
export enum CalculatorSettingKey {
  TIP_SHARES = 'tipShares',
  DEFAULT_POOL = 'defaultPool',
  SALES_GROUPS = 'salesGroups',
  PARTICIPANTS = 'participants',
  GRACE_PERIOD = 'gracePeriod',
  CREDIT_CARD_FEE = 'creditCardFee',
  BUSINESS_DAY = 'businessDay',
  DAY_SEGMENTS = 'daySegments',
  INTEGRATION_CONFIG = 'integrationConfig',
}

export enum SettingChangeType {
  /** Present now, absent in the snapshot. */
  ADDED = 'added',
  /** Present in the snapshot, absent now. */
  REMOVED = 'removed',
  /** Present in both, different value. */
  MODIFIED = 'modified',
}

export interface ISettingDifference {
  setting: CalculatorSettingKey;
  changeType: SettingChangeType;
  /** Human-readable identity, for the console and for support. e.g. 'Late Night (21:00–03:00)', 'toast · ticket_processing_time'. */
  label: string;
  /** The snapshot's value. Undefined when ADDED. */
  original?: unknown;
  /** The site's current value. Undefined when REMOVED. */
  current?: unknown;
  /** False when an original-settings replay cannot restore the snapshot's value (see removed integration config keys). */
  replayable: boolean;
}

export interface ISettingsComparison {
  /** The snapshot presence sweep — false for every payment created before day segments were captured. */
  complete: boolean;
  /** Derived: `differences.length > 0`. Never computed independently. */
  changed: boolean;
  /**
   * Derived: every reported difference can be restored by an original-settings replay. Read it only
   * when `changed` is true — with nothing to restore it is trivially true. False means choosing
   * Original cannot reproduce the payment exactly (see removed integration config keys).
   */
  fullyReplayable: boolean;
  /** Which snapshot fields were absent. Populated only when `complete` is false. */
  missingFields: string[];
  differences: ISettingDifference[];
}

/** The current side of the comparison, gathered from Redux at the moment the settings gate renders. */
export interface ICurrentCalculatorSettings {
  siteSettings: ISiteSettings;
  calculatorRules: ICalculatorRules;
  /**
   * The site's day segments. Pass the full configured list — the comparison filters it to the
   * payment date's weekday itself, matching how the snapshot was captured.
   */
  daySegments: IDaySegment[];
  /** Integrations as returned with the calculator's POS results (`posResults.settings.integrations`) — public config only. */
  integrations: IPOSIntegration[];
}
/* END -- Calculator Settings Gate & Comparison -- END */

export interface IPaymentAdjustment {
  firstName: string;
  lastName: string;
  employeeId: string;
  original: number;
  recalculated: number;
  difference: number;
  tips: number;
  autograts: number;
}
